
// These are injected by vite-version-plugin at build time.
// In dev mode they won't exist, so we fall back to 'dev'.
declare const __APP_VERSION__: string;
export const CURRENT_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Composable that detects when a new version of the app has been deployed.
 * It polls /version.json periodically and on route navigation.
 *
 * Only intended for use in the Admin dashboard.
 * Students should NOT see version notifications.
 */
export function useAppVersion() {
  const updateAvailable = ref(false);
  const newVersion = ref('');
  let intervalId: ReturnType<typeof setInterval> | null = null;
  let unregisterGuard: (() => void) | null = null;

  async function checkForUpdate(): Promise<void> {
    // Skip in dev mode — no version.json exists
    if (CURRENT_VERSION === 'dev') return;

    try {
      // Cache-bust the fetch so we always get the latest version.json
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
      });

      if (!response.ok) return;

      const data = await response.json();

      if (data.version && data.version !== CURRENT_VERSION) {
        updateAvailable.value = true;
        newVersion.value = data.version;
      }
    } catch {
      // Silently ignore — network errors shouldn't disrupt the app
    }
  }

  function refresh() {
    window.location.reload();
  }

  function startPolling() {
    // Initial check after a short delay (don't block app startup)
    setTimeout(checkForUpdate, 5000);

    // Periodic check
    intervalId = setInterval(checkForUpdate, CHECK_INTERVAL_MS);
  }

  function stopPolling() {
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  }

  onMounted(() => {
    startPolling();

    // Also check on every route navigation
    try {
      const router = useRouter();
      unregisterGuard = router.afterEach(() => {
        checkForUpdate();
      });
    } catch {
      // Router may not be available in some contexts
    }
  });

  onUnmounted(() => {
    stopPolling();
    if (unregisterGuard) {
      unregisterGuard();
    }
  });

  return {
    updateAvailable,
    newVersion,
    refresh,
    checkForUpdate,
  };
}
